import type { ComponentProps, MouseEvent, ReactNode } from 'react';

import css from '@playbook/atoms/button/styles.module.css';
import { Spinner } from '@playbook/atoms/spinner';
import { type Size, cn } from '@playbook/utils';

/**
 *	The stylesheet is `atoms/button`'s, not a copy of it — an icon button is the
 *	same box with the label taken out, and two files would drift the moment one
 *	variant changed. The unions below are this atom's own, so nothing here
 *	imports `atoms/button`'s React code.
 */

export type IconButtonVariant =
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'ghost'
	| 'destructive'
	| 'destructive-ghost';

const DefaultIconButtonVariant: IconButtonVariant = 'tertiary' as const;

export type IconButtonSize = Extract<Size, 'sm' | 'md'>;

const DefaultIconButtonSize: IconButtonSize = 'md' as const;

type IconButtonType = 'button' | 'reset' | 'submit';

const DefaultIconButtonType: IconButtonType = 'button' as const;

type IconButtonEventHandlers = {
	[K in keyof ComponentProps<'button'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'button'>[K];
};

interface IconButtonDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

/**
 *	ARIA is admitted the way `data-*` is, as on `atoms/button`: a button that
 *	opens a popover cannot know on its own whether what it opened is showing,
 *	so whatever arranges the two writes `aria-expanded` from outside.
 */
interface IconButtonAriaAttributes {
	[key: `aria-${string}`]: string | number | boolean | undefined;
}

interface IconButtonProperties
	extends
		IconButtonEventHandlers,
		IconButtonDataAttributes,
		IconButtonAriaAttributes {
	variant?: IconButtonVariant;
	size?: IconButtonSize;
	type?: IconButtonType;
	disabled?: boolean;

	/**
	 *	Swaps the icon for a spinner and stops the button from being pressed,
	 *	while staying focusable and announced as busy. `disabled` wins when both
	 *	are set.
	 */
	loading?: boolean;

	/**
	 *	What the button does, in words. There are none on screen, so this is its
	 *	only accessible name and is written to `aria-label`. Required: a button
	 *	announced as just "button" cannot be used without sight.
	 */
	label: string;

	icon: ReactNode;

	/**
	 *	The `id` of a popover this button opens, which the browser then toggles
	 *	and light-dismisses on its own, as on `atoms/button`. `modules/chat`
	 *	opens the menu behind its add button this way.
	 */
	popoverTarget?: string;
}

/** Cancelling the click stops the caller's handler and a form submit alike. */
function preventPress(event: MouseEvent<HTMLButtonElement>): void {
	event.preventDefault();
}

export function IconButton({
	variant = DefaultIconButtonVariant,
	size = DefaultIconButtonSize,
	type = DefaultIconButtonType,
	disabled = false,
	loading = false,

	label,

	icon,

	popoverTarget,

	onClick,

	...props
}: IconButtonProperties): ReactNode {
	const isLoading: boolean = loading && !disabled;

	const className: string = cn(
		css.button,
		css[variant],
		css[size],
		css.iconButton,
		isLoading && css.loading
	);

	return (
		<button
			{...props}
			type={type}
			popoverTarget={popoverTarget}
			aria-label={label}
			data-variant={variant}
			data-size={size}
			className={className}
			disabled={disabled}
			aria-busy={isLoading || undefined}
			aria-disabled={isLoading || undefined}
			onClick={isLoading ? preventPress : onClick}>
			{isLoading ? <Spinner size={size} /> : icon}
		</button>
	);
}
